/**
 * useNexusGraphData
 *
 * Fetches computed graph data for a single panel config from the metrics
 * API (FEATURE-01/02/03). Refetches when the metric, split, or filters
 * change. Aborts in-flight requests on change/unmount.
 *
 * Dependencies:
 * - /api/nexus/crm/graphs/metrics
 * - lib/nexus/graph-metrics (config types)
 */

'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import type { GraphPanelDatum } from '@/components/nexus/graph-panel'
import type { NexusGraphPanelConfig } from '@/lib/nexus/graph-metrics'

export function useNexusGraphData(
  config: NexusGraphPanelConfig,
  workspaceId: string | null | undefined
) {
  const [data, setData] = useState<GraphPanelDatum[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Stable dependency key so effect only re-runs on meaningful changes.
  const key = useMemo(
    () =>
      JSON.stringify({
        m: config.metric,
        s: config.split ?? null,
        f: config.filters,
        w: workspaceId ?? null
      }),
    [config.metric, config.split, config.filters, workspaceId]
  )

  useEffect(() => {
    if (!workspaceId) {
      setData([])
      setLoading(false)
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    const params = new URLSearchParams({
      workspaceId,
      metric: config.metric,
      range: config.filters.dateRange
    })
    if (config.split) params.set('split', config.split)
    if (config.filters.customStart) params.set('start', config.filters.customStart)
    if (config.filters.customEnd) params.set('end', config.filters.customEnd)
    if (config.filters.status) params.set('status', config.filters.status)
    if (config.filters.source) params.set('source', config.filters.source)
    if (config.filters.tag) params.set('tag', config.filters.tag)
    if (config.filters.campaign) params.set('campaign', config.filters.campaign)
    if (config.filters.stage) params.set('stage', config.filters.stage)

    setLoading(true)
    setError(null)

    fetch(`/api/nexus/crm/graphs/metrics?${params.toString()}`, {
      signal: controller.signal,
      cache: 'no-store'
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`)
        return res.json()
      })
      .then((payload: { data?: GraphPanelDatum[] }) => {
        setData(Array.isArray(payload.data) ? payload.data : [])
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setError(err instanceof Error ? err.message : 'Failed to load graph data')
        setData([])
        setLoading(false)
      })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return { data, loading, error }
}
